import type { HotKeyHandler, IsolateBarrier, StackItem } from './types';
import { isBarrier, isHandler } from './types';
import { focusManager } from './FocusManager';

export class StackManager {
    private static instance: StackManager;
    private globalStack: StackItem[] = [];
    private handlerById: Map<string, HotKeyHandler> = new Map();

    public static getInstance(): StackManager {
        if (!StackManager.instance) {
            StackManager.instance = new StackManager();
        }

        return StackManager.instance;
    }

    /**
     * Добавляет барьер изоляции в глобальный стек.
     * Барьер блокирует все hotkeys, зарегистрированные НИЖЕ него (раньше по времени).
     */
    public addBarrier(barrierId: string): void {
        const barrier: IsolateBarrier = {
            barrierId,
        };

        this.globalStack.push(barrier);
    }

    /**
     * Удаляет барьер изоляции из глобального стека
     */
    public removeBarrier(barrierId: string): void {
        const index = this.globalStack.findIndex(item => isBarrier(item) && item.barrierId === barrierId);
        if (index !== -1) {
            this.globalStack.splice(index, 1);
        }
    }

    public addHandler(handler: HotKeyHandler): void {
        this.globalStack.push(handler);
        this.handlerById.set(handler.id, handler);
    }

    public removeHandlerById(handlerId: string): void {
        const handler = this.handlerById.get(handlerId);

        if (handler) {
            const index = this.globalStack.indexOf(handler);
            if (index !== -1) {
                this.globalStack.splice(index, 1);
            }
            this.handlerById.delete(handlerId);
        }
    }

    public setHandlerCallback(handlerId: string, callback: (event: KeyboardEvent) => void): void {
        const handler = this.handlerById.get(handlerId);
        if (handler) {
            handler.callback = callback;
        }
    }

    public getActiveHandler(key: string): HotKeyHandler | null {
        const focusedComponentId = focusManager.getFocusedComponentId();

        // 1. Найти индекс первого активного барьера
        const sortedStack = [...this.globalStack].reverse();
        const barrierIndex = sortedStack.findIndex(item => isBarrier(item));

        // 2. Взять все элементы ВЫШЕ барьера (или все, если барьера нет)
        const itemsAboveBarrier = barrierIndex === -1 ? sortedStack : sortedStack.slice(0, barrierIndex);

        // 3. Фильтровать handlers: только handlers + по ключу
        const availableHandlers = itemsAboveBarrier
            // оставляем только хендлеры
            .filter(isHandler)

            // фильтруем все коллбеки у которых есть данный хоткей
            .filter(handler => handler.keys === key)

            // фильтруем все коллбеки, которые проходят проверку доступности
            .filter(handler =>
                handler.staticAvailabilityCheck !== undefined ? handler.staticAvailabilityCheck() : true,
            );

        if (availableHandlers.length === 0) {
            return null;
        }

        // 3. Если есть focused component, найти его handler
        if (focusedComponentId) {
            const focusedHandler = availableHandlers.find(h => h.componentId === focusedComponentId);
            if (focusedHandler) {
                return focusedHandler;
            }
        }

        return availableHandlers[0];
    }

    public getStack(key: string): StackItem[] {
        // Фильтруем глобальный стек по клавише
        return this.globalStack.filter(item => {
            if (isBarrier(item)) return true;

            return (item as HotKeyHandler).keys === key;
        });
    }

    // @example:
    // window.__hotKeysDebug.getStackInfo()
    public getDebugInfo(): {
        totalHandlers: number;
        totalBarriers: number;
        globalStack: Array<
            | {
                  type: 'handler';
                  id: string;
                  componentId: string;
                  keys: string;
                  handlerName?: string;
              }
            | {
                  type: 'barrier';
                  barrierId: string;
              }
        >;
    } {
        let totalHandlers = 0;
        let totalBarriers = 0;

        const sortedStack = [...this.globalStack].reverse();

        const globalStack = sortedStack.map(item => {
            if (isBarrier(item)) {
                totalBarriers++;
                const barrier = item as IsolateBarrier;

                return {
                    type: 'barrier' as const,
                    barrierId: barrier.barrierId,
                };
            } else {
                totalHandlers++;
                const h = item as HotKeyHandler;

                return {
                    type: 'handler' as const,
                    id: h.id,
                    handlerName: h.handlerName,
                    componentId: h.componentId,
                    keys: h.keys,
                };
            }
        });

        return {
            totalHandlers,
            totalBarriers,
            globalStack,
        };
    }

    public clearComponentStack(componentId: string): void {
        this.globalStack = this.globalStack.filter(
            item => isBarrier(item) || (item as HotKeyHandler).componentId !== componentId,
        );
    }

    // метод для тестов с очисткой состояния
    public resetForTesting(): void {
        this.globalStack = [];
        this.handlerById.clear();
    }
}

export const stackManager = StackManager.getInstance();
